import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButtonV2 from "../../../components/CustomButtonV2/CustomButtonV2.js";
import ModalUsuario from "../../../components/ModalUsuario/ModalUsuario.tsx";
import TabelaV2 from "../../../components/TabelaV2/TabelaV2.js";
import { emptyFiltroEmbarque } from "../../../contexts/ContextConstants.ts";
import { useFiltro } from "../../../contexts/FiltroContext.tsx";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import { useTabela } from "../../../contexts/Tabela.js";
import { useToast } from "../../../contexts/ToastContext.tsx";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import Layout from "../../../core/layout/Layout.tsx";
import useService from "../../../hooks/useService.ts";
import { Embarque } from "../../../models/Embarque.ts";
import {
	FiltroEmbarqueCompleto,
	FiltroEmbarqueCompletoEnum,
} from "../../../models/FiltroEmbarqueCompleto.ts";
import { buscarViagensFinalizadas } from "../../../services/ModuloGerenciador/Viagens.ts";
import FiltroViagens from "../Embarque/components/FiltroViagens/FiltroViagens.tsx";
import "./ViagensFinalizadasMotorista.css";

function ViagensFinalizadasMotorista() {
	const { profile, hasRegra, tabelaUsuario, setContextoEmbarque } =
		useProfile();
	const navigate = useNavigate();
	const { tabelaRedux } = useTabela();
	const { showToast } = useToast();
	const {
		filtroEmbarque,
		clearFiltros,
		setOrdenarPor,
		ordenarPor,
		isFiltroModified,
		normalizarFiltroEmbarque,
	} = useFiltro();

	const [semAutorizacao, setSemAutorizacao] = useState(false);
	const [viagensFinalizadas, setViagensFinalizadas] = useState<Embarque[]>([]);
	const [openModalUsuario, setOpenUsuario] = useState(false);

	const filtroMemoized = useMemo(() => {
		return normalizarFiltroEmbarque(filtroEmbarque);
	}, [filtroEmbarque, normalizarFiltroEmbarque]);

	const [isLoadingViagensFinalizadasInicial, buscarViagensInicial] = useService(
		() =>
			buscarViagensFinalizadas({
				token: profile?.token || "",
				ordenarPor: ordenarPor,
				filtro: {
					...emptyFiltroEmbarque,
					[FiltroEmbarqueCompletoEnum.Contexto]: "todos",
				},
			}),
		{
			autoStart: false,
			onData: (response) => {
				setViagensFinalizadas(response.data);
			},
			onError: () => {
				showToast("Erro ao carregar viagens finalizadas", "error");
			},
		}
	);

	const [isLoadingViagensFinalizadasFiltro, buscarViagensFiltro] = useService(
		({
			filtro,
			orderBy,
		}: {
			filtro: FiltroEmbarqueCompleto;
			orderBy?: string;
		}) =>
			buscarViagensFinalizadas({
				token: profile?.token || "",
				ordenarPor: orderBy ?? ordenarPor,
				filtro,
			}),
		{
			autoStart: false,
			onData: (response) => {
				setViagensFinalizadas(response.data);
			},
			onError: () => {
				showToast("Erro ao buscar viagens com filtro", "error");
			},
		}
	);

	useEffect(() => {
		if (profile?.token) {
			setSemAutorizacao(!hasRegra("Viagens Finalizadas Por Motorista"));
			setContextoEmbarque("todos");
			if (isFiltroModified()) {
				handleBuscarViagens();
			} else {
				buscarViagensInicial();
			}
		}
	}, [profile?.token, hasRegra]);

	const handleBuscarViagens = useCallback(() => {
		buscarViagensFiltro({
			filtro: filtroMemoized,
		});
	}, [buscarViagensFiltro, filtroMemoized]);

	const handleCloseUsuario = useCallback(() => {
		setOpenUsuario(false);
	}, []);

	const handleClickEdit = useCallback(() => {
		if (!!tabelaRedux.row && !!tabelaRedux.colunaSelecionada) {
			setContextoEmbarque(tabelaRedux.row.contexto);
			navigate(
				`/viagens/${tabelaRedux.row.contexto}/cadastro/${tabelaRedux.colunaSelecionada}`
			);
		} else {
			showToast("Selecione uma viagem para editar.", "error");
		}
	}, [tabelaRedux.colunaSelecionada, tabelaRedux.row, navigate]);

	const buscarViagensFiltroClipboard = useCallback(
		async (filtros: FiltroEmbarqueCompleto) => {
			filtros[FiltroEmbarqueCompletoEnum.Contexto] = "todos";
			buscarViagensFiltro({
				filtro: filtros,
			});
		},
		[buscarViagensFiltro]
	);

	const limparMultiplosFiltros = useCallback(() => {
		clearFiltros();
		buscarViagensInicial();
	}, [profile?.token]);

	const buscarOrdenacao = useCallback(
		(ordenarPor: string) => {
			setOrdenarPor(ordenarPor);
			buscarViagensFiltro({
				filtro: filtroMemoized,
				orderBy: ordenarPor,
			});
		},
		[buscarViagensFiltro, setOrdenarPor, filtroMemoized]
	);

	const isLoading = useMemo(() => {
		return (
			!profile?.token ||
			isLoadingViagensFinalizadasInicial ||
			isLoadingViagensFinalizadasFiltro
		);
	}, [
		profile?.token,
		isLoadingViagensFinalizadasInicial,
		isLoadingViagensFinalizadasFiltro,
	]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	return (
		<Layout title="Viagens Finalizadas por Motorista">
			<div className="content-container">
				<FiltroViagens
					key="filtro-viagens-finalizadas-motorista"
					buscarViagens={handleBuscarViagens}
					buscarViagensClipboard={buscarViagensFiltroClipboard}
					onClearFiltros={limparMultiplosFiltros}
					showConfig={false}
					showDiariaExcedida={false}
					disabled={isLoading}
				/>
				<TabelaV2
					key="tabela-viagens-finalizadas-motorista"
					classeSelecionado="idEmbarque"
					nomeClasse1="outer-wrapper-v2"
					nomeClasse2="table-v2"
					colunas={tabelaUsuario?.Embarque}
					dados={viagensFinalizadas}
					nome="embarque"
					nomeTable="Embarque"
					buscarOrdenacao={buscarOrdenacao}
					isLoading={isLoading}
				/>
			</div>
			<div className="footer-space-between">
				{viagensFinalizadas.length > 0 && (
					<>
						<CustomButtonV2
							classCss="custom-button-editar"
							classText="button-texto-editar"
							titulo="Editar"
							icone="iconeEdit"
							cor="#CDCDCD"
							onClick={handleClickEdit}
						/>
						<CustomButtonV2
							classCss="custom-button-user"
							classText="button-texto-user"
							titulo="Obter Usuário"
							cor="#FFFFFF"
							onClick={() => {
								if (tabelaRedux.usuarioAlteracao) {
									setOpenUsuario(true);
								} else {
									showToast("Usuário de alteração não encontrado", "error");
								}
							}}
						/>
					</>
				)}
			</div>

			<ModalUsuario
				titulo="Usuário"
				tituloConfirmar="OK"
				open={openModalUsuario}
				onClose={handleCloseUsuario}
			/>
		</Layout>
	);
}

export default ViagensFinalizadasMotorista;
